/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2021-12-21 10:21:40
 *
 *******************************************************/
//flow
import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Modal, Checkbox } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { reduxForm, reset, change } from 'redux-form';

import Options from 'src/components/views/theme-setup-v3/feedback-modal/options';
import OptionFeedback from 'src/components/views/theme-setup-v3/feedback-modal/option-feedback';
import { TAR_FEATURE, getFeedbackOptions } from 'src/constants/theme-setup';

import * as themeRevertAction from 'actions/theme-revert-actions';
import { prettyText, capitalizeFirstLetter } from 'src/helpers';

type State = {
  option: number,
  valueOption: number,
  actionState: string,
  dontShowSurveyAgain: boolean
};

type Props = { dataSetup: { theme: string, removeAll: boolean } } & WithI18nProps;
// DONE
class RevertThemeFeedback extends Component<Props, State> {
  state = {
    option: 0,
    valueOption: 1,
    actionState: 'selectOption',
    dontShowSurveyAgain: false,
    removeAll: false
  };

  componentWillReceiveProps(nextProps) {
    if (this.props.loading && !nextProps.loading && nextProps.success === true) {
      //go to after submit

      this.setState({ actionState: 'gotIt' });
    }
  }

  componentWillUnmount() {
    const { unmountComponent } = this.props;
    unmountComponent();
  }

  // Render view based on the option which customer chose
  renderSwitch(option: number) {
    const { valueOption, actionState } = this.state;
    const { dataSetup = {}, removeFeatures, i18n, active } = this.props;
    const { removeAll = false } = dataSetup;
    const feature =
      !_.isEmpty(removeFeatures) && removeFeatures.length === 1 ? removeFeatures[0] : 'Multiple';

    if (active) {
      if (actionState === 'gotIt') {
        if (feature === capitalizeFirstLetter(TAR_FEATURE.INSTANT_SEARCH, 'en') && option === 4) {
          return <p>{i18n.translate(`AdminHTML.ThemeSetup.FeedbackModal.option2_ThankYou`)}</p>;
        }
        return (
          <p>{i18n.translate(`AdminHTML.ThemeSetup.FeedbackModal.option${option}_ThankYou`)}</p>
        );
      } else {
        switch (option) {
          case 0:
            //Select option view
            return (
              <Options
                value={valueOption}
                handleChangeValueOption={this.handleChangeValueOption}
                removeFeatures={removeFeatures}
              />
            );
          case 1:
          case 2:
          case 3:
          case 4:
          case 5:
          case 6:
            return (
              <OptionFeedback
                option={option}
                removeAll={removeAll}
                removeFeatures={removeFeatures}
              />
            );
          default:
            return null;
        }
      }
    }
  }

  render() {
    const { option, actionState, dontShowSurveyAgain } = this.state;
    const { handleSubmit, pristine, loading, i18n, active, removeFeatures } = this.props;
    const suffixType =
      !_.isEmpty(removeFeatures) && removeFeatures.length === 1 ? removeFeatures[0] : 'Multiple';

    const primaryAction =
      actionState === 'selectOption'
        ? {
            loading: loading && !pristine,
            content: i18n.translate('AdminHTML.Common.Action.next'),
            onAction: this.nextFeedback
          }
        : actionState === 'submitData'
        ? {
            loading: loading,
            content: i18n.translate('AdminHTML.Common.Action.submit'),
            onAction: handleSubmit(this.handleSave.bind(this))
          }
        : {
            content: i18n.translate('AdminHTML.Common.Action.okGotIt'),
            onAction: this.handleCloseFeedbackModal
          };
    return (
      <Modal
        large
        open={active}
        onClose={this.handleCloseFeedbackModal}
        title={i18n.translate(`AdminHTML.ThemeSetup.FeedbackModal.title_${suffixType}`)}
        primaryAction={primaryAction}
        footer={
          <div style={{ color: 'var(--p-text-subdued)' }}>
            <Checkbox
              label={i18n.translate('AdminHTML.ThemeSetup.FeedbackModal.dismissModal')}
              checked={dontShowSurveyAgain}
              onChange={() => this.handleChangeDismissFlag(suffixType === 'All')}
            />
          </div>
        }>
        <div style={{ marginLeft: 'auto' }}></div>
        <Modal.Section>
          <div className="modal-content-revert-survey theme-setup-v3">
            {this.renderSwitch(option)}
          </div>
        </Modal.Section>
      </Modal>
    );
  }

  // Handle change state
  handleChangeState = (option: number) => {
    this.setState({ option });
  };

  // Handle change value option
  handleChangeValueOption = (checked, newValue) => {
    this.setState({ valueOption: parseInt(newValue) });

    if (parseInt(newValue) === 3) {
      const { dispatch } = this.props;
      dispatch(change('RevertThemeFeedback', 'optionsQuestion2', ['Yes']));
    }
  };

  // Handle next option
  nextFeedback = () => {
    const { valueOption } = this.state;
    const actionState = 'submitData';
    this.setState({ option: valueOption, actionState });
  };

  // Handle close modal
  handleCloseFeedbackModal = () => {
    const { dontShowSurveyAgain } = this.state;
    const { resetForm } = this.props;

    this.setState({ option: 0, valueOption: 1, actionState: 'selectOption' });
    resetForm();
    this.props.handleCloseFeedbackModal(dontShowSurveyAgain);
  };

  //Handle submit
  handleSave = data => {
    const {
      sendThemeRevertFeedback,
      dataSetup = {},
      i18n,
      revertTheme,
      themeRevert: themeRevertFromParent,
      removeFeatures
    } = this.props;
    const { valueOption } = this.state;
    const { theme } = dataSetup;
    const feature =
      !_.isEmpty(removeFeatures) && removeFeatures.length === 1 ? removeFeatures[0] : '';
    const optionList = getFeedbackOptions(i18n, feature);

    const option = _.find(optionList, item => item.id === valueOption);
    let feedback = {
      version: 'v3',
      removeFeatures
    };
    feedback.answer2 = prettyText(_.get(data, 'content', ''));
    feedback.answer1 = _.get(option, 'value', '');
    feedback.optionsQuestion1 = valueOption;
    if (valueOption === 5) {
      feedback.answer2 = _.get(data, 'optionsQuestion2[0]', 'Yes');
    }
    feedback.themeId =
      _.get(theme, 'id', '') ||
      _.get(revertTheme, 'id', '') ||
      _.get(themeRevertFromParent, 'id', '');
    feedback.themeName =
      _.get(theme, 'name', '') ||
      _.get(revertTheme, 'name', '') ||
      _.get(themeRevertFromParent, 'name', '');
    const [question1, question2] = getFeedbackQuestion(valueOption, removeFeatures);
    feedback.question1 = question1;
    feedback.question2 = question2;
    sendThemeRevertFeedback(feedback);
  };

  // Handle action on checkbox dismiss modal
  handleChangeDismissFlag = isAll => {
    const { dontShowSurveyAgain } = this.state;
    this.setState({ dontShowSurveyAgain: !dontShowSurveyAgain });
  };
}

const getFeedbackQuestion = (optionsQuestion1, removeFeatures) => {
  const suffixType =
    !_.isEmpty(removeFeatures) && removeFeatures.length === 1 ? removeFeatures[0] : 'Multiple';

  let question1 = '';
  switch (suffixType) {
    case 'FilterSearch':
      question1 = 'Why did you decide to remove Search page with filters from your theme?';
      break;
    case 'FilterCollection':
      question1 = 'Why did you decide to remove Collection page with filters from your theme?';
      break;
    case 'InstantSearch':
      question1 = 'Why did you decide to remove Instant search widget from your theme?';
      break;
    case 'Recommendation':
      question1 = 'Why did you decide to remove Recommendation widget from your theme?';
      break;
    case 'Multiple':
      question1 = 'Why did you decide to remove these functions from your theme?';
      break;
    default:
      break;
  }

  // const question1 = i18n.translate(`AdminHTML.ThemeSetup.FeedbackModal.title_${suffixType}`);
  let question2 = 'Describe your experience';
  if (optionsQuestion1 === 5) {
    question2 =
      'We completely understand your concern. Do you want to discuss your price concern with our sale rep?';
  }

  return [question1, question2];
};

const mapStateToProps = (state, props) => ({
  loading: _.get(state.themeRevert, 'feedbackLoading', false),
  success: _.get(state.themeRevert, 'feedbackSuccess', false),
  revertTheme: _.get(state, 'themeSetupV3.revertTheme', null),
  error: state.setup.error,
  removeFeatures: _.get(state.themeSetupV3, 'removeFeatures', [])
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  sendThemeRevertFeedback: (body: Object) =>
    dispatch(themeRevertAction.sendThemeRevertFeedback(body, ownProps.i18n)),
  unmountComponent: () => dispatch(themeRevertAction.unmountComponent()),
  resetForm: () => dispatch(reset('RevertThemeFeedback'))
});

const RevertThemeFeedbackForm = reduxForm({
  form: 'RevertThemeFeedback', // a unique identifier for this form
  enableReinitialize: true
})(RevertThemeFeedback);

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(RevertThemeFeedbackForm));
